import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEventStream } from '../hooks/useEventStream';
import { useDashboard } from '../context/DashboardContext';
import { Event } from '../types';
import { StatCard } from '../components/StatCard';
import { EventTable } from '../components/EventTable';
import { SessionTable } from '../components/SessionTable';
import { TrapList } from '../components/TrapList';
import { FilterBar } from '../components/FilterBar';
import { EventDetailsDrawer } from '../components/EventDetailsDrawer';
import { Header } from '../components/Header';
import './Dashboard.css';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, siteConfig, logout } = useAuth();

  // IMPORTANT: in REST-only hook, reconnect() just refetches
  const { events, sessions, traps, stats, connected, reconnect } = useEventStream();

  const { filters, updateFilter } = useDashboard();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Refresh UI state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);

  // Optional auto refresh
  const [autoRefresh, setAutoRefresh] = useState(false);

  const doRefresh = async () => {
    try {
      setIsRefreshing(true);
      await Promise.resolve(reconnect());
      setLastRefresh(new Date().toISOString());
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto refresh every 5 seconds (toggle)
  useEffect(() => {
    if (!autoRefresh) return;

    const id = window.setInterval(() => {
      doRefresh();
    }, 5000);

    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  // Filter events based on active filters
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (filters.severity !== 'all' && event.severity !== filters.severity) return false;
      if (filters.eventType !== 'all' && event.action !== filters.eventType) return false;

      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        if (
          !event.sourceIp.toLowerCase().includes(searchLower) &&
          !event.path.toLowerCase().includes(searchLower)
        ) {
          return false;
        }
      }

      if (filters.selectedSessionId && event.sessionId !== filters.selectedSessionId) return false;

      return true;
    });
  }, [events, filters]);

  const handleSessionClick = (sessionId: string) => {
    updateFilter('selectedSessionId', sessionId);
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };

  const closeDrawer = () => {
    setSelectedEvent(null);
  };

  return (
    <div className="dashboard">
      {/* Animated Background */}
      <div className="dashboard-background">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
      </div>

      {/* Header */}
      <Header
        showConnectionBadge={true}
        connected={connected}
        showRefreshButtons={true}
        isRefreshing={isRefreshing}
        autoRefresh={autoRefresh}
        onRefresh={doRefresh}
        onToggleAutoRefresh={() => setAutoRefresh((v) => !v)}
        lastRefresh={lastRefresh}
      />

      {/* Stats Grid */}
      <section className="stats-grid">
        <StatCard title="Total Requests" value={stats.totalRequests} icon="📊" color="primary" />
        <StatCard title="Suspicious Sessions" value={stats.suspiciousSessions} icon="⚠️" color="warning" />
        <StatCard title="Active Traps" value={stats.activeTraps} icon="🎯" color="danger" />
        <StatCard title="Trapped Sessions" value={stats.trappedSessions} icon="🔒" color="success" />
      </section>

      {/* Main Grid */}
      <div className="main-grid">
        {/* Events Section */}
        <div className="grid-item events-container">
          <div className="card">
            <div className="card-header">
              <div className="card-title-group">
                <h2 className="card-title">
                  Live Event Stream
                  {lastRefresh && (
                    <span style={{ fontSize: '0.85rem', opacity: 0.7, fontWeight: 600 }}>
                      (last refresh: {new Date(lastRefresh).toLocaleString('en-US', {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })})
                    </span>
                  )}
                </h2>
                <span className="badge badge-orange">{filteredEvents.length}</span>
              </div>
              <FilterBar />
            </div>
            <div className="card-body">
              <div className="table-wrapper">
                <EventTable events={filteredEvents} onEventClick={handleEventClick} />
              </div>
            </div>
          </div>
        </div>

        {/* Sessions Section */}
        <div className="grid-item sessions-container">
          <div className="card">
            <div className="card-header">
              <div className="card-title-group">
                <h2 className="card-title">Active Sessions</h2>
                <span className="badge badge-green">{sessions.length}</span>
              </div>
            </div>
            <div className="card-body">
              <SessionTable
                sessions={sessions}
                onSessionClick={handleSessionClick}
                selectedSessionId={filters.selectedSessionId}
              />
            </div>
          </div>
        </div>

        {/* Traps Section */}
        <div className="grid-item traps-container">
          <TrapList traps={traps} />
        </div>
      </div>

      {/* Event Details Drawer */}
      <EventDetailsDrawer event={selectedEvent} onClose={closeDrawer} />
    </div>
  );
};
